import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, RefreshCw, Check, Trash2, Loader2, BookOpen } from "lucide-react";
import { Link } from "react-router-dom";
import { fetchTranslations, startTranslation, approveTranslation, deleteTranslation, formatApiError } from "@/lib/api";

const Row = ({ t, onAction }) => {
  const running = t.job?.status === "running";
  const pct = running && t.job.total ? Math.round((t.job.done / t.job.total) * 100) : 0;
  return (
    <div className="admin-block flex items-center gap-4" data-testid={`lang-row-${t.lang}`}>
      <div style={{ width: 200 }}>
        <div className="font-head" style={{ fontSize: 20, color: "#fff" }} dir="auto">{t.native_name}</div>
        <div className="text-[10px] uppercase tracking-widest opacity-50">{t.name} · {t.lang} · {t.dir}</div>
      </div>
      <div className="flex-1 min-w-0">
        {t.source && <span className="text-xs opacity-70">Source edition · written by the editorial team</span>}
        {!t.source && !t.exists && !running && <span className="text-xs opacity-60">Not generated yet</span>}
        {!t.source && t.exists && !running && (
          <span className="text-xs" data-testid={`lang-status-${t.lang}`}>
            {t.pages} pages · {t.review_status === "approved" ? <span style={{ color: "#9BE7A8" }}>reviewed & approved</span> : <span style={{ color: "#E8C27A" }}>AI translation · pending native-speaker review</span>}
          </span>
        )}
        {running && (
          <div data-testid={`lang-progress-${t.lang}`}>
            <div className="text-xs mb-1 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Translating… {t.job.done}/{t.job.total}</div>
            <div style={{ height: 3, background: "rgba(217,218,221,0.12)" }}><div style={{ width: `${pct}%`, height: 3, background: "var(--platinum)", transition: "width .4s ease" }} /></div>
          </div>
        )}
        {t.job?.status === "error" && <div className="text-xs mt-1" style={{ color: "#F09595" }}>Error: {t.job.error}</div>}
      </div>
      <div className="flex items-center gap-2">
        {t.exists && <Link to={`/read/${t.lang}`} target="_blank" className="btn-silver" style={{ padding: "6px 10px" }} data-testid={`lang-open-${t.lang}`}><BookOpen size={12} /> Open</Link>}
        {!t.source && (
          <button className="btn-bordeaux" style={{ padding: "6px 12px" }} disabled={running} onClick={() => onAction("generate", t.lang)} data-testid={`lang-generate-${t.lang}`}>
            {t.exists ? <><RefreshCw size={12} /> Regenerate</> : <><Sparkles size={12} /> Generate</>}
          </button>
        )}
        {!t.source && t.exists && !running && (
          <button className="btn-silver" style={{ padding: "6px 10px" }} onClick={() => onAction(t.review_status === "approved" ? "unapprove" : "approve", t.lang)} data-testid={`lang-approve-${t.lang}`}>
            <Check size={12} /> {t.review_status === "approved" ? "Mark pending" : "Approve"}
          </button>
        )}
        {!t.source && t.exists && !running && (
          <button className="reader-btn" title="Delete edition" onClick={() => onAction("delete", t.lang)} data-testid={`lang-delete-${t.lang}`}><Trash2 size={14} /></button>
        )}
      </div>
    </div>
  );
};

export const LanguagesPanel = () => {
  const [rows, setRows] = useState([]);
  const load = () => fetchTranslations().then(setRows).catch(() => {});

  useEffect(() => {
    load();
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, []);

  const onAction = async (action, lang) => {
    try {
      if (action === "generate") { await startTranslation(lang); toast.success(`Translation started for ${lang.toUpperCase()}`); }
      if (action === "approve") { await approveTranslation(lang, "approved"); toast.success("Marked as reviewed & approved"); }
      if (action === "unapprove") { await approveTranslation(lang, "pending"); toast.success("Marked as pending review"); }
      if (action === "delete") {
        if (!window.confirm(`Delete the ${lang.toUpperCase()} edition and all its pages?`)) return;
        await deleteTranslation(lang); toast.success("Edition deleted");
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="py-6 max-w-5xl" data-testid="languages-panel">
      <div className="mb-6">
        <div className="admin-label">Language editions</div>
        <h2 className="font-head" style={{ fontSize: 34, color: "#fff", lineHeight: 1 }}>Languages</h2>
        <p className="text-sm mt-3 opacity-70 max-w-2xl">
          New editions are translated from the English source by AI (Emergent LLM key) and marked “pending native-speaker review”. Every page can be corrected in the page editor; approve an edition once it has been reviewed.
        </p>
      </div>
      <div className="space-y-3">
        {rows.map((t) => <Row key={t.lang} t={t} onAction={onAction} />)}
      </div>
    </div>
  );
};
